// src/components/Fireworks.tsx
import React, { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  alpha: number;
}

const Fireworks: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles: Particle[] = [];

    const colors = [
      '#ff0000', '#ff8000', '#ffff00f2', '#00ff00',
      '#00ffff', '#0080ff', '#8000ff', '#ff00ff',
      '#ff4080', '#40ff80', '#40c0ff'
    ];

    const createFirework = () => {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height * 0.5 + 50;
      const color = colors[Math.floor(Math.random() * colors.length)];
      const particleCount = 60 + Math.random() * 40;

      for (let i = 0; i < particleCount; i++) {
        const angle = (Math.PI * 2 * i) / particleCount;
        const velocity = 2 + Math.random() * 6;

        particles.push({
          x,
          y,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity,
          color,
          alpha: 1,
        });
      }
    };

    let nextFirework = Date.now() + 3000;

    const animate = () => {
      // Fully transparent clear — this is the magic line
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const now = Date.now();
      if (now > nextFirework) {
        createFirework();
        nextFirework = now + Math.random() * 6000 + 2000; // every 3–10 sec
      }

      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.1;    // gravity
        p.alpha -= 0.012;

        if (p.alpha <= 0) {
          particles.splice(i, 1);
          return;
        }

        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 15;
        ctx.shadowColor = p.color;
        ctx.fillRect(p.x - 3, p.y - 3, 6, 6);
      });

      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',   // super important — lets you click through
        zIndex: 9999,
        background: 'transparent',
      }}
    />
  );
};

export default Fireworks;